// src/component/datatable2/components/ActiveFiltersBar.tsx

import React from 'react';
import { useTranslation } from 'react-i18next';
import { classNames } from 'primereact/utils';

import type { ActiveFilterChip } from '../models/ActiveFilterChips';
import { FilterChip } from './FilterChip';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import './ActiveFiltersBar.scss';

/** What the bar needs from a chip; DataTable2's ActiveFilterChip and the report chips both fit. */
export type ActiveFiltersBarChip = Pick<ActiveFilterChip, 'name' | 'nameLabel' | 'valueLabel'>;

interface Props<TChip extends ActiveFiltersBarChip> {
    chips: TChip[];
    onRemove: (chip: TChip) => void;
    onClearAll: () => void;
    className?: string;
}

/**
 * "Filters:" row above a table: one removable chip per filter value, plus "Clear all".
 * Used by DataTable2 and by the report grid pages, so active filters look the same everywhere.
 */
export const ActiveFiltersBar = <TChip extends ActiveFiltersBarChip>({ chips, onRemove, onClearAll, className }: Props<TChip>) => {
    const { t } = useTranslation();

    if (chips.length === 0) {
        return null;
    }

    return (
        <div className={classNames('filters dt2-active-filters', className)} role="group" aria-label={t('dataTable.filters')}>
            <span className="label">{t('dataTable.filters')}</span>

            {chips.map((chip, index) => (
                <FilterChip
                    key={`${chip.name}-${chip.valueLabel}-${index}`}
                    label={chip.nameLabel ? `${chip.nameLabel}: ${chip.valueLabel}` : chip.valueLabel}
                    removable
                    onClick={() => onRemove(chip)}
                />
            ))}

            <FilterChip
                key="filter-clear-all"
                className="clear"
                label={t('dataTable.clearAll')}
                onClick={onClearAll}
            />
        </div>
    );
};
